import { Injectable, InjectionToken, Provider } from '@angular/core';
import { PaginatedResponse } from '../../shared/pagination/paginated-response';
import { ApiContact, CreateApiContactBody } from '../api-types/contact.api';
import { Contact, CreateContactBody, UpdateContactBody } from '../app-types/contact.app';
import {
	transformApiContactsToAppContacts,
	transformApiContactToAppContact,
	transformAppUpdateContactBodyToApiContactBody,
	transformCreateContactToCreateApiContactBody,
} from '../transformers/contact.transform';
import { BaseRequestService } from './base-request.service';
import { ApiResponse, Metadata } from '../api-types/api-response.api';

export const CONTACT_API = new InjectionToken<ContactApi>('CONTACT_API');

@Injectable()
export class ContactApi extends BaseRequestService {
	public getAll = async (clientId: number, assignmentId: number, offset?: number, range?: number) => {
		const apiContacts = await this.get<PaginatedResponse<ApiContact>>(
			`clients/${clientId}/assignments/${assignmentId}/contacts`,
			{ Offset: offset, Range: range },
		);
		const contacts = transformApiContactsToAppContacts(apiContacts.entities);
		const totalContacts = apiContacts.totalEntities;
		return {
			contacts: contacts,
			totalContacts: totalContacts,
		};
	};

	public getSingle = async (clientId: number, assignmentId: number, contactId: number) => {
		const response = await this.get<ApiResponse<ApiContact, Metadata>>(
			`clients/${clientId}/assignments/${assignmentId}/contacts/${contactId}`,
		);
		const result = transformApiContactToAppContact(response.data);
		return { data: result, metadata: response.metadata };
	};

	public create = async (clientId: number, assignmentId: number, data: CreateContactBody) => {
		const body = transformCreateContactToCreateApiContactBody(data);
		return await this.post<CreateApiContactBody, CreateApiContactBody>(
			`clients/${clientId}/assignments/${assignmentId}/contacts`,
			body,
		);
	};

	public update = async (clientId: number, contactId: number, data: UpdateContactBody) => {
		const body = transformAppUpdateContactBodyToApiContactBody(data);
		const response = await this.put<UpdateContactBody, UpdateContactBody>(
			`clients/${clientId}/contacts/${contactId}`,
			body,
		);
		return response;
	};

	public getDashboardContacts = async (
		offset?: number,
		range?: number,
		userId?: string,
	): Promise<PaginatedResponse<Contact>> => {
		const response = await this.get<PaginatedResponse<ApiContact>>('dashboard/contacts', {
			offset,
			range,
			userId,
		});
		const entities = transformApiContactsToAppContacts(response.entities);
		return {
			...response,
			entities: entities,
		};
	};

	public getNetworkContact = async (contactId: number) => {
		const response = await this.get<ApiContact>(`clients/contacts/${contactId}`);
		return transformApiContactToAppContact(response);
	};

	public updateNetworkContact = async (contactId: number, body: UpdateContactBody) => {
		const payload = transformAppUpdateContactBodyToApiContactBody(body);
		const response = await this.put<UpdateContactBody, UpdateContactBody>(`clients/contacts/${contactId}`, payload);
		return response;
	};
}

export const provideContactApi: Provider = {
	provide: CONTACT_API,
	useClass: ContactApi,
};
